'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { isAudioAttachment, isImageAttachment } from '@/contracts/mediaAttachments'
import { selectMainAttachmentsState } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'

function bytesLabel(size?: number) {
  if (!size || size < 1) return ''
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function MusicAttachmentPlayer() {
  const { state } = useSubsStore()
  const attachments = useMemo(() => selectMainAttachmentsState(state), [state])
  const tracks = useMemo(() => attachments.filter(item => item.mediaKind === 'audio' || isAudioAttachment(item.filename || item.src)), [attachments])
  const otherFiles = useMemo(() => attachments.filter(item => (
    !tracks.includes(item)
    && item.mediaKind !== 'image'
    && !isImageAttachment(item.filename || item.src)
  )), [attachments, tracks])
  const [selectedId, setSelectedId] = useState('')
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (!tracks.length) { setSelectedId(''); return }
    if (!tracks.some(track => track.id === selectedId)) setSelectedId(tracks[0].id)
  }, [selectedId, tracks])

  const selected = tracks.find(track => track.id === selectedId) || tracks[0]
  const selectAndPlay = (id: string) => {
    setSelectedId(id)
    window.setTimeout(() => { void audioRef.current?.play().catch(() => {}) }, 0)
  }

  return (
    <section className="mb-7 rounded-3xl bg-[#241a14] p-4 text-[#fffaf3] shadow-xl sm:p-6" aria-label="Audio tracks">
      <div className="mb-4 flex items-end justify-between gap-3"><div><p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#d7b894]">Attached audio</p><h2 className="text-xl font-semibold">Tracks</h2></div><span className="text-xs text-[#bca894]">{tracks.length}</span></div>
      {selected ? <div className="mb-4 rounded-2xl bg-white/10 p-3"><div className="mb-2 truncate font-medium">{selected.title || selected.filename || 'Audio track'}</div>{selected.description ? <p className="mb-2 text-sm text-[#d9c9bb]">{selected.description}</p> : null}<audio key={selected.src} ref={audioRef} className="w-full" controls preload="metadata" src={selected.src}>{selected.mime ? <source src={selected.src} type={selected.mime} /> : null}</audio></div> : <p className="rounded-2xl border border-white/15 p-4 text-sm text-[#d9c9bb]">No playable audio attachments yet.</p>}
      {tracks.length ? <ol className="space-y-1">{tracks.map((track, index) => <li key={`${track.id}-${track.legacyIndex}`}><button type="button" className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left ${track.id === selected?.id ? 'bg-[#6b3f24]' : 'hover:bg-white/10'}`} onClick={() => selectAndPlay(track.id)}><span className="w-6 text-right text-xs text-[#d7b894]">{index + 1}</span><span className="min-w-0 flex-1 truncate text-sm font-medium">{track.title || track.filename || `Track ${index + 1}`}</span>{track.size ? <span className="text-[10px] text-[#bca894]">{bytesLabel(track.size)}</span> : null}<span aria-hidden="true">▶</span></button></li>)}</ol> : null}
      {otherFiles.length ? <div className="mt-5 border-t border-white/15 pt-4"><h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#d7b894]">Other attachments</h3><div className="flex flex-wrap gap-2">{otherFiles.map(file => <a key={file.id} className="rounded-full bg-white/10 px-3 py-1.5 text-xs hover:bg-white/20" href={file.src}>{file.title || file.filename || 'Attachment'}</a>)}</div></div> : null}
    </section>
  )
}
